import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { JoinCta, ValidatorCta } from "@/components/Buttons";
import { scrollToSection } from "@/lib/modal";
import { MessageCircle, Youtube, ShieldCheck } from "lucide-react";

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const EASE = [0.22, 1, 0.36, 1];

const NAV_LINKS = [
  { label: "How It Works", href: "#how-it-works", testid: "footer-link-network" },
  { label: "Verifiers", href: "#validators", testid: "footer-link-validators" },
  { label: "Enterprise", href: "#enterprise", testid: "footer-link-enterprise" },
  { label: "Roadmap", href: "#roadmap", testid: "footer-link-roadmap" },
];

const SOCIALS = [
  { label: "X", icon: XLogo, href: "https://x.com/Synaphy", testid: "social-x" },
  { label: "YouTube", icon: Youtube, href: "https://www.youtube.com/@Synaphy", testid: "social-youtube" },
  { label: "Discord", icon: MessageCircle, href: "https://discord.com/channels/1551156605595164743/1551156606199275533", testid: "social-discord" },
];

export const Closing = () => {
  return (
    <>
      <section className="relative py-32 sm:py-48 overflow-hidden bg-ink-900/40" data-testid="section-final-cta">
        <div className="absolute left-1/2 bottom-[-30%] -translate-x-1/2 h-[480px] w-[820px] rounded-full bg-signal-violet/[0.07] blur-[160px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE }}
            className="font-display font-bold tracking-tight leading-[1.04] text-5xl sm:text-6xl lg:text-7xl text-mist max-w-4xl"
            data-testid="final-cta-headline"
          >
            The physical world is waiting to be{" "}
            <span className="text-gradient-data">understood.</span>
          </motion.h2>
          <Reveal delay={0.2}>
            <p className="mt-7 text-lg text-steel max-w-lg">
              Join the network while it is being built.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-11 flex flex-col sm:flex-row sm:items-center gap-4">
              <JoinCta testid="final-join-btn" className="justify-center sm:justify-start" />
              <ValidatorCta testid="final-validator-btn" className="justify-center sm:justify-start" />
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="relative border-t border-white/5 bg-ink-950" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-10">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-12">
            <div>
              <div className="flex items-center">
                <img src="/logo-lockup.png" alt="Synaphy - Intelligence for the physical world" className="h-20 w-auto object-contain drop-shadow-[0_0_20px_rgba(90,150,255,0.35)]" />
              </div>
              <p className="mt-5 text-sm text-steel" data-testid="footer-tagline">
                Capture. Understand. Verify.
              </p>
              <div className="mt-6 flex items-center gap-2">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={s.testid}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-steel transition-colors duration-300 hover:text-signal-cyan hover:bg-white/[0.04]"
                  >
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            <nav className="flex flex-wrap items-start gap-x-8 gap-y-3 lg:pt-2" aria-label="Footer">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollToSection(l.href)}
                  data-testid={l.testid}
                  className="text-sm font-medium text-silver-glass transition-[filter] duration-300 hover:brightness-125"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/portal"
                data-testid="footer-link-portal"
                className="text-sm font-medium text-silver-glass transition-[filter] duration-300 hover:brightness-125"
              >
                Portal
              </Link>
              <Link to="/privacy" data-testid="footer-link-privacy" className="text-sm font-medium text-silver-glass transition-[filter] duration-300 hover:brightness-125">Privacy</Link>
              <Link to="/terms" data-testid="footer-link-terms" className="text-sm font-medium text-silver-glass transition-[filter] duration-300 hover:brightness-125">Terms</Link>
              <a href="mailto:contact@synaphy.com" data-testid="footer-link-contact" className="text-sm font-medium text-silver-glass transition-[filter] duration-300 hover:brightness-125">Contact</a>
            </nav>

            <div className="font-display text-2xl font-semibold tracking-tight leading-snug text-silver-glass lg:text-right" data-testid="footer-vision">
              A safer, smarter
              <br />
              physical world.{" "}
              <span className="text-gradient-data">Together.</span>
            </div>
          </div>

          <div className="mt-16 border-t border-white/5 pt-8">
            <div className="mb-6 max-w-3xl" data-testid="footer-legal-status">
              <div className="flex items-center gap-2.5 mb-2.5">
                <ShieldCheck className="h-4 w-4 text-signal-cyan" />
                <span className="text-sm font-semibold text-mist">Legal status: Pre-launch / Founding phase</span>
              </div>
              <p className="text-xs leading-relaxed text-zinc-600">
                Legal entity and jurisdiction details will be published at Pilot launch.
              </p>
            </div>
            <p className="max-w-3xl text-xs leading-relaxed text-zinc-600" data-testid="footer-disclaimer">
              Synaphy is currently in its Founding / pre-launch phase. Features, network
              economics, verifier requirements and reward mechanisms may change as the
              protocol develops. Nothing on this website constitutes a promise of
              financial returns.
            </p>
            <p className="mt-8 font-display text-lg sm:text-xl font-semibold tracking-tight" data-testid="footer-motto">
              <span className="text-silver-glass">Work Together.</span>{" "}
              <span className="text-silver-glass">Grow Together.</span>{" "}
              <span className="text-gradient-data">Succeed Together.</span>
            </p>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-xs text-zinc-700">
                © {new Date().getFullYear()} Synaphy. All rights reserved.
              </p>
              <p className="text-xs text-zinc-700">
                Built for a more intelligent planet.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};
